<!--
  REDEEM-02 门票 / 演唱会兑换弹窗
  状态机:F-0 场次月历 → F-1 填表 → F-2 inline 报错 → F-3 校验通过 → F-4 二确
          → F-success / F-5 售空 popup
  对外契约(P03 + P04 锁定,不得改):
    props: { visible, resultPublicId, prizeTitle? }
    emits: close / redemption-complete / share-success / wishlist-add

  D-24 合规:
    - shared 包零业务依赖;API 调用 + events fetch 由父容器(user-prize-list/render/index.vue)
      通过 props 注入:events 数组 via props,submit 闭包 via props.submit
    - i18n 文案 via shared/utils/i18n.ts
  T-01-05-02 XSS:opponentShort / teamText 均走 Vue `{{ }}` 自动转义;无 v-html 出现。
-->
<template>
  <!-- Vue 2.6 要求单根;多态 PanelModal 用 v-if 同时只渲染一个,逻辑上仍然单 modal -->
  <div class="redeem-ticket-modal-root">
    <!-- F-0:场次日历 -->
    <PanelModal
      v-if="visible && isCalendarStep"
      :visible="true"
      :page-type="pageType"
      panel-background="linear-gradient(180deg, #0a489d 0%, #042258 100%)"
      :content-padded="false"
      :show-close-button="pageType === 'PC'"
      :panel-width="pageType === 'PC' ? '760px' : ''"
      class="calendar-panel-modal"
      @close="onPanelClose"
    >
    <EventCalendar
      :events="events"
      :page-type="pageType"
      :month="calendarMonth"
      @event-selected="onEventSelected"
      @month-change="onMonthChange"
    />
  </PanelModal>

  <!-- F-1/F-2/F-3:填表 -->
  <PanelModal
    v-if="visible && isFormPanelVisible"
    :visible="true"
    :page-type="pageType"
    :content-padded="false"
    :show-close-button="pageType === 'PC'"
    @close="onPanelClose"
  >
    <div
      v-if="isFillStep"
      class="fill-form"
      :class="`page-${pageType}`"
    >
      <h2 class="title">{{ t('Modal.fillTitle') }}</h2>
      <p class="subtitle">{{ t('Modal.fillSubtitle') }}</p>
      <FormItem
        class="first-form-item"
        :label="t('Modal.nameLabel')"
        :value="form.name.value"
        :placeholder="t('Modal.namePlaceholder')"
        :error="form.errors.value.name"
        :page-type="pageType"
        @update:value="onNameInput"
        @blur="onNameBlur"
      />
      <FormItem
        :label="t('Modal.emailLabel')"
        :value="form.email.value"
        :placeholder="t('Modal.emailPlaceholder')"
        :error="form.errors.value.email"
        type="email"
        :page-type="pageType"
        @update:value="onEmailInput"
        @blur="onEmailBlur"
      />
      <div class="cta">
        <button
          type="button"
          class="btn-ghost"
          @click="onBackToCalendar"
        >
          {{ t('Modal.back') }}
        </button>
        <button
          type="button"
          class="btn-primary"
          :class="{ disabled: !form.canSubmit.value }"
          :disabled="!form.canSubmit.value"
          @click="onConfirmFromForm"
        >
          {{ t('Modal.confirm') }}
        </button>
      </div>
      <div
        v-if="pageType === 'H5'"
        class="home-indicator"
        aria-hidden="true"
      />
    </div>
  </PanelModal>

  <!-- 二次确认:居中 NoticeModal,保持单实例避免 teleport 切换时 DOM 失配 -->
  <NoticeModal
    v-if="visible && isNoticeStep"
    :visible="true"
    :page-type="pageType"
    :variant="noticeVariant"
    :text-align="noticeTextAlign"
    :title="noticeTitle"
    :description="noticeDescription"
    :button-text="noticeButtonText"
    @close="onNoticeClose"
  >
    <ConfirmSheet
      v-if="isConfirmStep"
      :page-type="pageType"
      :variant="confirmVariant"
      :subtitle="t('Modal.confirmSubtitleTicket')"
      :rows="confirmRows"
      :embedded="true"
      :show-header="false"
      @edit="onEdit"
      @confirm="form.handleConfirm()"
    />
  </NoticeModal>

  <!-- F-5 售空:复用成功票卡结构,仅替换文案并追加右上角水印 -->
  <PanelModal
    v-if="visible && isPcSoldOutStep"
    :visible="true"
    :page-type="pageType"
    :content-padded="false"
    :show-home-indicator="true"
    @close="onSoldOutOk"
  >
    <template #badge>
      <div
        v-if="pageType === 'PC'"
        class="sold-out-corner-badge"
      >
        <span class="sold-out-stamp">Sold Out</span>
      </div>
    </template>
    <SuccessSheet
      :page-type="pageType"
      :variant="successVariant"
      :prize-title="prizeTitle"
      :rows="successRows"
      :home-team="homeTeam"
      :away-team="awayTeam"
      :subtitle-key="successSubtitleKey"
      state="sold-out"
      text-align="center"
      @close="onSoldOutOk"
      @share-friends="onShareFriends"
    />
  </PanelModal>

  <!-- F-wish:wish 场次详情 -->
  <PanelModal
    v-if="visible && isWishStep"
    :visible="true"
    :page-type="pageType"
    :content-padded="false"
    :badge-src="wishBadge"
    badge-alt="wish"
    @close="onPanelClose"
  >
    <WishlistSheet
      :page-type="pageType"
      :variant="successVariant"
      :rows="ticketPreviewRows"
      :home-team="homeTeam"
      :away-team="awayTeam"
      :added="isWishlistAdded"
      :show-toast="wishToastVisible"
      @add="onWishlistAdd"
    />
  </PanelModal>

  <!-- F-success -->
  <PanelModal
    v-if="visible && isSuccessStep"
    :visible="true"
    :page-type="pageType"
    :content-padded="false"
    :show-home-indicator="true"
    @close="onSuccessClose"
  >
    <SuccessSheet
      :page-type="pageType"
      :variant="successVariant"
      :prize-title="prizeTitle"
      :rows="successRows"
      :home-team="homeTeam"
      :away-team="awayTeam"
      :subtitle-key="successSubtitleKey"
      state="success"
      text-align="center"
      @close="onSuccessClose"
      @share-friends="onShareFriends"
    />
  </PanelModal>

  </div>
</template>

<script lang="ts">
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from 'vue-demi';
// Vue2 仓内惯例:require(...).use 避免 ESM interop

import PanelModal from './PanelModal.vue';
import NoticeModal from './NoticeModal.vue';
import FormItem from './FormItem.vue';
import ConfirmSheet from './ConfirmSheet.vue';
import SuccessSheet from './SuccessSheet.vue';
import WishlistSheet from './WishlistSheet.vue';
import EventCalendar from './EventCalendar.vue';
import { useRedeemForm } from '../composables/useRedeemForm';
import t from '../utils/i18n';
import wishBadge from '../assets/badge-ticket-wish.png';

/**
 * 场次列表元素(shared 包不引 @ark/mets-shared 业务 types,此处行内复刻与
 * GetEventsRsp['events'][number] 等价,保持 D-24 纯净。)
 */
type EventItem = {
  eventSign: string;
  eventDate: number;
  eventType: 'match' | 'concert';
  opponentShort?: string;
  opponentFull?: string;
  teamText?: string;
  status: 'available' | 'hot' | 'sold-out' | 'wish' | 'empty';
  wishlistAdded?: boolean;
};
type TicketVariant = 'ticket' | 'concert';

/**
 * 父容器闭包:POST /giveaway/redeem-ticket
 * shared 组件只产出表单与场次字段;seasonSign 由父容器闭包补齐。
 */
type TicketSubmitFn = (req: {
  resultPublicId: string;
  eventSign: string;
  contactInfo: {
    name: string;
    email: string;
  };
}) => Promise<{
  success: boolean;
}>;
type WishlistSubmitFn = (req: {
  eventSign: string;
  eventType: TicketVariant;
}) => Promise<{
  success: boolean;
}>;
type Props = {
  visible: boolean;
  resultPublicId: string;
  /** F-4/F-success 行展示用;父容器 onFillOut 时查 prizes 填入 */
  prizeTitle?: string;
  /**
   * 父注入的场次列表(父容器用 useEvents 拉 GET /inventory/redeemable-events)
   * 空数组 → EventCalendar 显示月内空态，但保留切月入口
   */
  events?: EventItem[];
  /** 父容器持有的当前日历月份，格式 YYYY-MM。 */
  calendarMonth?: string;
  /**
   * 日历由页面承载(如 ticket-redemption);弹窗从填表/心愿详情起步,Back 关闭弹窗。
   */
  skipCalendar?: boolean;
  /** sold-out 场次点击后直接展示售罄提示（不含 wish，wish 走 wish-detail） */
  nonRedeemableToSoldOut?: boolean;
  /** skipCalendar 时由页面传入已选场次快照 */
  preselectedEvent?: EventItem | null;
  /**
   * 父注入的 submit 闭包(父容器包装 POST /giveaway/redeem-ticket)
   * 未传时 handleConfirm 抛错(开发期守门)
   */
  submit?: TicketSubmitFn;
  /** 父注入的 wishlist 提交闭包;真实 API 契约未落位时可返回本地成功 */
  wishlistSubmit?: WishlistSubmitFn;
};
const __sfc_main = {};
__sfc_main.props = {
  visible: {
    key: "visible",
    required: true,
    type: Boolean
  },
  resultPublicId: {
    key: "resultPublicId",
    required: true,
    type: String
  },
  prizeTitle: {
    key: "prizeTitle",
    required: false,
    type: String,
    default: ''
  },
  events: {
    key: "events",
    required: false,
    type: Array,
    default: () => []
  },
  calendarMonth: {
    key: "calendarMonth",
    required: false,
    type: String,
    default: ''
  },
  skipCalendar: {
    key: "skipCalendar",
    required: false,
    type: Boolean,
    default: false
  },
  nonRedeemableToSoldOut: {
    key: "nonRedeemableToSoldOut",
    required: false,
    type: Boolean,
    default: false
  },
  preselectedEvent: {
    key: "preselectedEvent",
    required: false,
    type: null,
    default: null
  },
  submit: {
    key: "submit",
    required: false,
    type: null,
    default: undefined
  },
  wishlistSubmit: {
    key: "wishlistSubmit",
    required: false,
    type: null,
    default: undefined
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const {
    usePageType,
    useToast,
    useZIndex
  } = (require('@fegobull/webapi') as any).use;
  const props = __props;
  const emit = __ctx.emit;
  const instance = getCurrentInstance();

  /** Vue 2.6 + 组合式 API 下 defineEmits 偶发未挂载到实例,兜底 $emit */
  function emitToParent(event: 'close'): void {
    emit(event);
    instance?.proxy?.$emit?.(event);
  }
  const {
    isPC
  } = usePageType() as any;
  const Toast = useToast();
  const pageType = computed<'PC' | 'H5'>(() => {
    const isPcValue = isPC && typeof isPC === 'object' && 'value' in isPC ? isPC.value : isPC;
    return isPcValue ? 'PC' : 'H5';
  });

  /** F-0 选中的场次快照;F-1..F-4 / F-success 渲染均读之 */
  const selectedEvent = ref<EventItem | null>(null);
  const wishAddedByEventSign = ref<Record<string, boolean>>({});
  const wishToastVisible = ref(false);
  let wishToastTimer: ReturnType<typeof setTimeout> | null = null;

  // 闭包注入 submit:useRedeemForm 的 SubmitFn 零参,这里构造 payload 再调父 prop
  // 注意:form 变量通过 const 在下一行 assign;handleConfirm 调用时才求值 form.name/email
  const form = useRedeemForm({
    submit: async () => {
      if (!props.submit) {
        throw new Error('RedeemTicketModal: `submit` prop is required at runtime. ' + 'user-prize-list/render/index.vue must pass redeem-ticket closure.');
      }
      if (!selectedEvent.value) {
        // 理论不可达(F-0 未选场次时 handleConfirm 不会被触发)
        throw new Error('RedeemTicketModal: no event selected');
      }
      return props.submit({
        resultPublicId: props.resultPublicId,
        eventSign: selectedEvent.value.eventSign,
        contactInfo: {
          name: form.name.value.trim(),
          email: form.email.value.trim()
        }
      });
    }
  });

  // P05 比 Physical 多一个场次选择起点;useRedeemForm 默认填表,这里覆盖
  form.setStep('event-selection');
  function applyModalEntryStep(): void {
    if (props.skipCalendar && props.preselectedEvent) {
      selectedEvent.value = props.preselectedEvent;
      if (props.preselectedEvent.status === 'sold-out') {
        form.setStep('sold-out');
        return;
      }
      form.setStep(props.preselectedEvent.status === 'wish' ? 'wish-detail' : 'form-input');
      return;
    }
    selectedEvent.value = null;
    form.setStep('event-selection');
  }
  function syncModalEntryFromProps(): void {
    if (!props.visible) return;
    form.reset();
    hideWishToast();
    applyModalEntryStep();
  }

  // 父级 v-if 挂载时 visible 已为 true，watch 不会触发，需 onMounted 同步入口步骤
  onMounted(() => {
    syncModalEntryFromProps();
  });
  watch(() => props.visible, (v, prev) => {
    if (v && prev === false) {
      syncModalEntryFromProps();
    }
  });
  onUnmounted(() => {
    hideWishToast();
  });
  const isFillStep = computed(() => form.step.value === 'form-input' || form.step.value === 'form-invalid' || form.step.value === 'form-valid');
  const isCalendarStep = computed(() => !props.skipCalendar && form.step.value === 'event-selection');
  const isWishStep = computed(() => form.step.value === 'wish-detail');
  const isConfirmStep = computed(() => form.step.value === 'confirm');
  const isSuccessStep = computed(() => form.step.value === 'success');
  const isSoldOutStep = computed(() => form.step.value === 'sold-out');
  const isPcSoldOutStep = computed(() => isSoldOutStep.value && pageType.value === 'PC');
  const isNoticeStep = computed(() => isConfirmStep.value || isSoldOutStep.value && pageType.value !== 'PC');
  // 与 Physical 一致：二确时保留填表 Panel 挂载，避免 Panel 卸载后 Notice teleport 叠层失败
  const isFormPanelVisible = computed(() => isFillStep.value || isConfirmStep.value);
  const noticeVariant = computed<'confirm' | 'alert'>(() => isSoldOutStep.value ? 'alert' : 'confirm');
  const noticeTextAlign = computed<'left' | 'center'>(() => isSoldOutStep.value ? 'center' : 'left');
  const noticeTitle = computed(() => isSoldOutStep.value ? t('Modal.soldOutTitle') : t('Modal.confirmTitle'));
  const noticeDescription = computed(() => isSoldOutStep.value ? t('Modal.soldOutSubtitle') : t('Modal.confirmSubtitleTicket'));
  const noticeButtonText = computed(() => isSoldOutStep.value ? t('Modal.ok') : '');

  /** 门票展示变体:concert → concert;其他(baseball / 缺省)→ ticket */
  const ticketVariant = computed<TicketVariant>(() => selectedEvent.value?.eventType === 'concert' ? 'concert' : 'ticket');
  const confirmVariant = computed<TicketVariant>(() => ticketVariant.value);
  const successVariant = computed<TicketVariant>(() => ticketVariant.value);
  const successSubtitleKey = computed<'successSubtitleTicket' | 'successSubtitleConcert'>(() => ticketVariant.value === 'concert' ? 'successSubtitleConcert' : 'successSubtitleTicket');
  const isWishlistAdded = computed<boolean>(() => {
    const ev = selectedEvent.value;
    if (!ev) return false;
    return Boolean(ev.wishlistAdded || wishAddedByEventSign.value[ev.eventSign]);
  });
  function formatEventDate(ts: number): string {
    // 赛事时间数据源为美东(ET),展示必须强制 America/New_York 时区,
    // 否则 CN 时区 / 夏威夷等非 ET 用户会看到 +/-N 小时的错乱时间。
    // UI-SPEC §4.6.c 样例「2026.04.30/16:00」即 ET 本地语义。
    const d = new Date(ts * 1000);
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const parts: Record<string, string> = {};
    for (const p of fmt.formatToParts(d)) {
      if (p.type !== 'literal') parts[p.type] = p.value;
    }
    // Intl 在部分 runtime 可能把 00 时返回为 24,统一归一
    const hour = parts.hour === '24' ? '00' : parts.hour;
    return `${parts.year}.${parts.month}.${parts.day}/${hour}:${parts.minute}`;
  }
  function formatEventDateCard(ts: number): string {
    const d = new Date(ts * 1000);
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      day: '2-digit',
      month: 'short',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const parts: Record<string, string> = {};
    for (const p of fmt.formatToParts(d)) {
      if (p.type !== 'literal') parts[p.type] = p.value;
    }
    const hour = parts.hour === '24' ? '00' : parts.hour;
    return `${parts.day} ${parts.month} - ${parts.weekday} - ${hour}:${parts.minute}`;
  }
  function stripMarkup(text: string): string {
    return text.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  }
  const homeTeam = computed(() => 'Mets');
  const awayTeam = computed(() => {
    const ev = selectedEvent.value;
    if (!ev) return 'Miami';
    const raw = ev.opponentFull || ev.opponentShort || '';
    const clean = stripMarkup(raw);
    return clean || 'Miami';
  });

  /**
   * F-4 / F-success 行组装
   * - ticket(baseball):6 行 Match / Time / Venue / Ticket / Name / Email
   * - concert:5 行 Time / Venue / Ticket / Name / Email(无 Match)
   * Venue / Ticket 文案迁入 i18n dict(rowValueVenueCitiField / rowValueTicketSuite),
   * 破除原文件硬编英文串;redeemQuantity 未来从后端返回后替换 rowValueTicketSuite。
   */
  const confirmRows = computed(() => {
    const ev = selectedEvent.value;
    const rows: Array<{
      label: string;
      value: string;
      highlight?: boolean;
    }> = [];
    if (ticketVariant.value === 'ticket' && ev?.teamText) {
      rows.push({
        label: t('Modal.rowLabelMatch'),
        value: ev.teamText
      });
    }
    rows.push({
      label: t('Modal.rowLabelTime'),
      value: ev ? formatEventDate(ev.eventDate) : ''
    });
    rows.push({
      label: t('Modal.rowLabelVenue'),
      value: t('Modal.rowValueVenueCitiField')
    });
    rows.push({
      label: t('Modal.rowLabelTicket'),
      value: t('Modal.rowValueTicketSuite'),
      highlight: true
    });
    rows.push({
      label: t('Modal.rowLabelName'),
      value: form.name.value.trim()
    });
    rows.push({
      label: t('Modal.rowLabelEmail'),
      value: form.email.value.trim()
    });
    return rows;
  });

  /** F-success / F-wish 票卡下方信息 3 行:Time / Venue / Ticket(UI-SPEC §4.8.a) */
  const ticketPreviewRows = computed(() => {
    const ev = selectedEvent.value;
    return [{
      label: t('Modal.rowLabelTime'),
      value: ev ? formatEventDateCard(ev.eventDate) : ''
    }, {
      label: t('Modal.rowLabelVenue'),
      value: t('Modal.rowValueVenueCitiField')
    }, {
      label: t('Modal.rowLabelTicket'),
      value: t('Modal.rowValueTicketSuite')
    }];
  });
  const successRows = ticketPreviewRows;
  function onNameInput(v: string): void {
    form.setFieldValue('name', v);
  }
  function onEmailInput(v: string): void {
    form.setFieldValue('email', v);
  }
  function onNameBlur(): void {
    form.validateField('name');
  }
  function onEmailBlur(): void {
    form.validateField('email');
  }
  function onEventSelected(payload: EventItem): void {
    // EventCalendar 直接 emit 完整 EventItem(WR-02),teamText/opponentFull 无损透传,
    // 不再经 find(...) 二次查表,避免 events 并发 refetch 时的 race 丢字段。
    selectedEvent.value = payload;
    if (props.nonRedeemableToSoldOut && payload.status === 'sold-out') {
      form.setStep('sold-out');
      return;
    }
    if (payload.status === 'wish') {
      form.setStep('wish-detail');
      return;
    }
    form.setStep('form-input');
  }
  function onMonthChange(month: string): void {
    selectedEvent.value = null;
    emit('month-change', {
      month
    });
  }
  function hideWishToast(): void {
    if (wishToastTimer) {
      clearTimeout(wishToastTimer);
      wishToastTimer = null;
    }
    wishToastVisible.value = false;
  }
  function showWishToast(): void {
    hideWishToast();
    wishToastVisible.value = true;
    wishToastTimer = setTimeout(() => {
      wishToastVisible.value = false;
      wishToastTimer = null;
    }, 2400);
  }
  async function onWishlistAdd(): Promise<void> {
    const ev = selectedEvent.value;
    if (!ev || isWishlistAdded.value) return;
    try {
      if (props.wishlistSubmit) {
        await props.wishlistSubmit({
          eventSign: ev.eventSign,
          eventType: ticketVariant.value
        });
      }
      wishAddedByEventSign.value = {
        ...wishAddedByEventSign.value,
        [ev.eventSign]: true
      };
      emit('wishlist-add', {
        eventSign: ev.eventSign,
        eventType: ticketVariant.value,
        added: true
      });
      showWishToast();
    } catch {
      Toast.error({
        message: t('Error.unknown'),
        zIndex: useZIndex()
      });
    }
  }
  function onConfirmFromForm(): void {
    if (form.validate()) {
      form.setStep('confirm');
    }
  }
  function onEdit(): void {
    // UI-SPEC §4.6:Edit 关闭二确回填表态(字段保留)
    form.setStep('form-input');
  }
  function onNoticeClose(): void {
    if (isSoldOutStep.value) {
      onSoldOutOk();
      return;
    }
    onEdit();
  }
  function onBackToCalendar(): void {
    if (props.skipCalendar) {
      // 页内日历模式:Back 关闭弹窗,页面日历仍可见
      emitToParent('close');
      return;
    }
    // 填表 Back:回场次选择,字段清空避免跨场次串值
    selectedEvent.value = null;
    form.reset();
    form.setStep('event-selection');
  }
  function onPanelClose(): void {
    emitToParent('close');
  }
  function onSuccessClose(): void {
    emit('redemption-complete', {
      resultPublicId: props.resultPublicId,
      prizeType: ticketVariant.value
    });
    emitToParent('close');
  }
  function onShareFriends(): void {
    emit('share-success', {
      resultPublicId: props.resultPublicId,
      prizeType: ticketVariant.value
    });
  }
  function onSoldOutOk(): void {
    if (props.skipCalendar) {
      // 页内日历模式:售罄后直接关弹窗,避免 Notice 遮罩残留挡住填表按钮
      emitToParent('close');
      return;
    }
    // Giveaway 弹窗内:OK 回到填表态,字段保留,用户可改邮箱重试
    form.setStep('form-input');
  }
  return {
    t,
    wishBadge,
    pageType,
    wishToastVisible,
    form,
    isFillStep,
    isCalendarStep,
    isWishStep,
    isConfirmStep,
    isSuccessStep,
    isPcSoldOutStep,
    isNoticeStep,
    isFormPanelVisible,
    noticeVariant,
    noticeTextAlign,
    noticeTitle,
    noticeDescription,
    noticeButtonText,
    confirmVariant,
    successVariant,
    successSubtitleKey,
    isWishlistAdded,
    homeTeam,
    awayTeam,
    confirmRows,
    ticketPreviewRows,
    successRows,
    onNameInput,
    onEmailInput,
    onNameBlur,
    onEmailBlur,
    onEventSelected,
    onMonthChange,
    onWishlistAdd,
    onConfirmFromForm,
    onEdit,
    onNoticeClose,
    onBackToCalendar,
    onPanelClose,
    onSuccessClose,
    onShareFriends,
    onSoldOutOk
  };
};
__sfc_main.components = Object.assign({
  PanelModal,
  EventCalendar,
  FormItem,
  NoticeModal,
  ConfirmSheet,
  SuccessSheet,
  WishlistSheet
}, __sfc_main.components);
export default __sfc_main;
</script>

<style>
/* ─── 场次日历 PC 边框（teleport 到 body，需用非 scoped 或准确穿透） ─── */
.calendar-panel-modal .panel-modal-panel.page-PC {
  border: 1px solid #ffffff;
}
</style>

<style scoped>

/* ─── 填表区通用 ──────────────────────────────────────── */
.fill-form {
  display: flex;
  flex-direction: column;
}
.btn-primary {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  background: #ff6900;
  color: #ffffff;
  border: none;
  cursor: pointer;
  font-family: BR Hendrix, Arial, sans-serif;
}
.btn-primary.disabled,
.btn-primary:disabled {
  background: #d7d7d7;
  color: #ffffff;
  cursor: not-allowed;
}
.btn-ghost {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  background: transparent;
  color: #1d1d1d;
  cursor: pointer;
  font-family: BR Hendrix, Arial, sans-serif;
}
.cta {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 2;
}

/* ─── H5(rem,设计稿 100px=1rem)UI-SPEC §4.3 ──────────── */
.fill-form {
  padding: 0;
  gap: 0;
}
.fill-form .title {
  font-weight: 600;
  font-size: 0.5rem;
  line-height: 1.1em;
  letter-spacing: -0.02em;
  color: #000000;
  margin: 0;
  padding: 0.26rem 0.4rem 0;
}
.fill-form .subtitle {
  font-weight: 400;
  font-size: 0.3rem;
  line-height: 1.4em;
  color: #626262;
  margin: 0.24rem 0.4rem 0;
  text-align: left;
}
.fill-form .cta {
  padding: 0.2308rem 0.4rem;
  gap: 0.1923rem;
}
.fill-form .btn-ghost {
  width: 2.04rem;
  height: 0.9rem;
  border-radius: 2.24rem;
  border: 2px solid #1d1d1d;
  font-weight: 600;
  font-size: 0.36rem;
}
.fill-form .btn-primary {
  width: 4.54rem;
  height: 0.9rem;
  border-radius: 0.8rem;
  font-weight: 600;
  font-size: 0.36rem;
}
.fill-form .home-indicator {
  height: 0.32rem;
}

/* ─── PC 覆盖 UI-SPEC §4.3 ────────────────────────────── */
.fill-form.page-PC {
  padding: 0;
  gap: 20px;
  width: 100%;
}
.fill-form.page-PC .title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  padding: 0;
  margin: 0;
  text-align: left;
  letter-spacing: -0.02em;
  color: #000000;
}
.fill-form.page-PC .subtitle {
  font-size: 18px;
  line-height: 1.4;
  margin: -16px 0 0 0;
  color: #626262;
  text-align: left;
}
.fill-form.page-PC .cta {
  justify-content: center;
  gap: 10px;
  margin-top: 0;
  padding: 0;
}
.fill-form.page-PC .btn-ghost {
  width: 160px;
  height: 60px;
  border-radius: 60px;
  border: 1px solid #1d1d1d;
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1d;
}
.fill-form.page-PC .btn-primary {
  width: 280px;
  height: 60px;
  border-radius: 60px;
  font-size: 24px;
  font-weight: 600;
}

/* 售空水印挂在 PanelModal badge slot,坐标以 PC 弹窗右上角为基准。 */
.sold-out-corner-badge {
  position: relative;
  width: 130.55px;
  height: 118px;
  overflow: hidden;
  border-radius: 0 20px 0 0;
  background: #ffffff;
}

.sold-out-corner-badge::before {
  content: '';
  position: absolute;
  left: -27px;
  top: -77px;
  width: 223px;
  height: 223px;
  box-sizing: border-box;
  border: 2.68px solid rgba(151, 151, 151, 0.4);
  border-radius: 50%;
}

.sold-out-corner-badge::after {
  content: '';
  position: absolute;
  left: -5px;
  top: -55px;
  width: 178px;
  height: 178px;
  box-sizing: border-box;
  border: 1.34px solid rgba(151, 151, 151, 0.4);
  border-radius: 50%;
}

.sold-out-stamp {
  position: absolute;
  left: 55px;
  top: 67px;
  z-index: 1;
  width: 112px;
  color: rgba(151, 151, 151, 0.4);
  font-family: Anton, Arial, sans-serif;
  font-size: 30px;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  text-transform: uppercase;
  transform: rotate(-28deg);
  transform-origin: center;
}
</style>
